// TimelineYearView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    DAY_MS,
    formatTimelineRangeText,
    getInclusiveEndOffsetMs,
    getScheduledBounds,
    getScheduledRows,
    getTimelineBarStyle,
    getUnscheduledLabel,
    resolveViewportTimelineBounds,
} from '../shared/timelineViewUtils';

// CSS
import './TimelineYearView.css';

const YEAR_COLUMN_WIDTH = 144;
const MIN_TRACK_WIDTH = 720;
const MIN_VISIBLE_YEARS = Math.ceil(MIN_TRACK_WIDTH / YEAR_COLUMN_WIDTH);

const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const addYears = (date, years) => {
    const next = new Date(date);
    next.setFullYear(next.getFullYear() + years);
    return next;
};

const startOfYear = (date) => {
    const next = startOfDay(date);
    next.setMonth(0, 1);
    return next;
};

const endOfYear = (date) => {
    const nextYearStart = startOfYear(addYears(date, 1));
    return new Date(nextYearStart.getTime() - 1);
};

/**
 * TimelineYearView
 *
 * Right-side timeline renderer for year-scale timeline.
 *
 * Top row only: Years
 *
 * @returns {ReactNode} The rendered component.
 */
const TimelineYearView = React.memo(({
    timelineRows = [],
    timelineFieldDef,
    viewportBounds = null,
    onNodeClick,
    onNodeDoubleClick,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    // ----- STATE ----- //

    const [loadedTrailingYears, setLoadedTrailingYears] = useState(0);
    // ----- REFS ----- //

    const scrollRef = useRef(null);
    const lastLoadTriggerWidthRef = useRef(0);

    // ----- MEMOS ----- //

    const scheduledRows = useMemo(() => getScheduledRows(timelineRows), [timelineRows]);

    const scheduledSpanResetToken = useMemo(() => {
        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return `none_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
        return `${scheduledBounds.minStart.getTime()}_${scheduledBounds.maxEnd.getTime()}_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
    }, [scheduledRows, timelineFieldDef?.key, timelineFieldDef?.name]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setLoadedTrailingYears(0);
        lastLoadTriggerWidthRef.current = 0;
    }, [scheduledSpanResetToken]);

    // ----- MEMOS ----- //

    const bounds = useMemo(() => {
        const viewport = resolveViewportTimelineBounds(viewportBounds);
        if (viewport) return viewport;

        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return null;

        const startYear = startOfYear(scheduledBounds.minStart);
        const dataEndYear = startOfYear(scheduledBounds.maxEnd);
        const minSpanEndYear = startOfYear(addYears(startYear, MIN_VISIBLE_YEARS - 1));
        const baseEndYear = dataEndYear.getTime() < minSpanEndYear.getTime()
            ? minSpanEndYear
            : dataEndYear;
        const endYear = loadedTrailingYears > 0
            ? startOfYear(addYears(baseEndYear, loadedTrailingYears))
            : baseEndYear;

        const start = startYear;
        const end = endOfYear(endYear);
        return { start, end };
    }, [scheduledRows, loadedTrailingYears, viewportBounds]);

    // ----- HANDLERS ----- //

    const handleScroll = useCallback(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const nearRightEdge = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 48;
        if (!nearRightEdge) return;

        if (lastLoadTriggerWidthRef.current === scrollEl.scrollWidth) return;
        lastLoadTriggerWidthRef.current = scrollEl.scrollWidth;
        setLoadedTrailingYears((prev) => prev + 1);
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => handleScroll();
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [handleScroll]);

    // ----- MEMOS ----- //

    const columns = useMemo(() => {
        if (!bounds) return [];

        const out = [];
        let cursor = startOfYear(bounds.start);

        while (cursor.getTime() <= bounds.end.getTime()) {
            const yearStart = new Date(cursor);
            const next = startOfYear(addYears(cursor, 1));
            const yearEnd = new Date(next.getTime() - 1);

            out.push({
                key: `y_${yearStart.getTime()}`,
                start: yearStart,
                end: yearEnd,
                label: yearStart.toLocaleDateString('en-US', { year: 'numeric' }),
            });

            cursor = next;
        }

        return out;
    }, [bounds]);

    const trackWidth = useMemo(() => (
        Math.max(MIN_TRACK_WIDTH, (columns.length || 1) * YEAR_COLUMN_WIDTH)
    ), [columns.length]);

    const spanMs = useMemo(() => {
        if (!bounds) return 0;
        return Math.max(1, bounds.end.getTime() - bounds.start.getTime());
    }, [bounds]);

    if (!bounds || columns.length === 0) {

        return (
            <div className="timeline-year-view-container">

                {/* EMPTY */}
                <div
                  className="timeline-year-view-empty"
                >
                    No scheduled timeline items yet.
                </div>
            </div>
        );
    }
    // ----- RENDER ----- //

    return (
        <div
          className="timeline-year-view-container"
          style={{
                '--timeline-year-column-width': `${YEAR_COLUMN_WIDTH}px`,
            }}
        >

            {/* SCROLL */}
            <div className="timeline-year-view-scroll" ref={scrollRef}>

                {/* TIMELINE YEAR HEADER */}
                <div
                  className="timeline-year-header"
                  style={{
                        borderBottom: `1px solid ${theme.status}`,
                        width: `${trackWidth}px`,
                    }}
                >

                    {/* TIMELINE YEAR HEADER ROW TOP */}
                    <div className="timeline-year-header-row timeline-year-header-row-top">
                        {columns.map((column) => (
                            <div
                              className="timeline-year-header-cell timeline-year-header-cell-top"
                              style={{
                                width: `${YEAR_COLUMN_WIDTH}px`,
                              }}
                              key={column.key}
                              title={column.label}
                            >
                                {column.label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* TIMELINE YEAR BODY */}
                <div
                  className="timeline-year-body"
                  style={{
                    width: `${trackWidth}px`,
                  }}
                >
                    {timelineRows.map((row, rowIndex) => {
                        const rowDividerStyle = rowIndex > 0
                            ? { borderTop: `1px solid ${theme.status}` }
                            : undefined;

                        if (row.type === 'group-header') {
                            return (
                                <div
                                  className="timeline-year-row timeline-year-row-group"
                                  style={{
                                    ...rowDividerStyle,
                                  }}
                                  key={row.key}
                                />
                            );
                        }

                        const entry = row.entry;
                        const isUnscheduled = row.type === 'item-unscheduled';
                        const barStyle = getTimelineBarStyle({
                            entry,
                            bounds,
                            spanMs,
                            trackWidth,
                            minDurationMs: DAY_MS,
                            minWidthPercent: 1.1,
                            inclusiveEndMs: getInclusiveEndOffsetMs(timelineFieldDef?.type),
                        });
                        const rangeText = formatTimelineRangeText(entry?.range, timelineFieldDef?.type);
                        const showBarLabel = barStyle?.widthPx >= 120;

                        return (
                            <div
                              className="timeline-year-row timeline-year-row-item"
                              style={{
                                    ...rowDividerStyle,
                                    backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${YEAR_COLUMN_WIDTH}px)`,
                                }}
                              key={row.key}
                              onClick={(event) => onNodeClick?.(event, entry?.node)}
                              onDoubleClick={(event) => onNodeDoubleClick?.(event, entry?.node)}
                            >

                                {/* TIMELINE YEAR UNSCHEDULED */}
                                {isUnscheduled && (
                                    <div
                                      className="timeline-year-unscheduled"
                                    >
                                        {getUnscheduledLabel(timelineFieldDef)}
                                    </div>
                                )}

                                {!isUnscheduled && barStyle && (
                                    <div
                                      className={`timeline-year-bar${entry?.range?.isMilestone ? ' is-milestone' : ''}`}
                                      style={{
                                            backgroundColor: entry?.barColor || theme.highlight,
                                            color: entry?.barForeground || undefined,
                                            left: barStyle.left,
                                            width: barStyle.width,
                                        }}
                                      title={rangeText}
                                    >

                                        {/* TIMELINE YEAR BAR LABEL */}
                                        {showBarLabel && (
                                            <span
                                              className="timeline-year-bar-label"
                                            >{rangeText}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

// Dev Tools
TimelineYearView.displayName = 'TimelineYearView';

export default TimelineYearView;
